import { Button, Form, Modal } from 'react-bootstrap';
import { RatingStars } from '../RatingStars/RatingStars';
import { useEffect, useMemo, useState } from 'react';
import styles from './Feedback.module.scss';
import { useFirebase } from '../../../contexts/FirebaseContext';
import { Else, If, Then, When } from 'react-if';
import { Link } from 'react-router-dom';
import { HighlightedCard } from '../HighlightedCard/HighlightedCard';
import { useTranslation } from 'react-i18next';

const RatingBox = ({ label, setter, disabled = false }) => (
  <div className={`${styles.feedbackRatingBox} d-flex flex-column align-items-start p-1`}>
    <div>{label}</div>
    <RatingStars onRate={setter} disabled={disabled} />
  </div>
);

const FeedbackComponent = ({ show, onHide }) => {
  const { database, fv, currentUser, userDataFmt } = useFirebase();
  const { t } = useTranslation();

  const [loading, setLoading] = useState(false);
  const [feedbackSent, setFeedbackSent] = useState(false);

  const [rating1, setRating1] = useState<number>(null);
  const [rating2, setRating2] = useState<number>(null);
  const [rating3, setRating3] = useState<number>(null);
  const [comment, setComment] = useState('');

  const questions = useMemo(
    () => [
      { label: t('feedback.labels.facilitaFruizione'), rating: rating1, setter: setRating1 },
      { label: t('feedback.labels.qualitaContenuti'), rating: rating2, setter: setRating2 },
      { label: t('feedback.labels.esperienzaPiattaforma', { title: process.env.REACT_APP_TITLE }), rating: rating3, setter: setRating3 },
    ],
    [rating1, rating2, rating3, t]
  );

  const getFullName = () => {
    const getProp = (prop) => userDataFmt?.profile?.formData?.[prop]?.trim?.();
    return `${getProp('nome')} ${getProp('cognome')}`.trim() || null;
  };

  useEffect(() => {
    if (show) return;
    questions.forEach((q) => q.setter(null));
    setComment('');
    setLoading(false);
    setFeedbackSent(false);
  }, [show]);

  return (
    <Modal className="tootor-modal" show={show} onHide={() => onHide(false)} centered backdrop={'static'} keyboard={false} scrollable>
      <Modal.Header>
         <Modal.Title>{t('feedback.labels.titolo')}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
         <p>{t('feedback.labels.opinioneImportante')}</p>
        <p>
           {t('feedback.labels.descrizione', { title: process.env.REACT_APP_TITLE })}
        </p>
        <If condition={feedbackSent}>
          <Then>
               <HighlightedCard className="p-3">
                 <h5>{t('feedback.labels.inviatoConSuccesso')}</h5>
                 <div>{t('feedback.labels.grazieContributo', { title: process.env.REACT_APP_TITLE })}</div>
              </HighlightedCard>
          </Then>
          <Else>
             <HighlightedCard className={`p-3 ${styles.helpCallout}`}>
               <div className={'font-weight-bold'}>{t('feedback.labels.haiBisognoAiuto')}</div>
               <div>
                 {t('feedback.labels.contattaciTramite')}{' '}
                 <Link to={'/help'} onClick={() => onHide(false)}>
                   <span className={styles.helpLink}>{t('labels.helpCenter')}</span>
                 </Link>
                 .
              </div>
             </HighlightedCard>
            {questions.map((q, i) => (
              <RatingBox key={i.toString()} label={q.label} setter={q.setter} disabled={loading} />
            ))}
             <p className={'mt-3'}>{t('feedback.labels.aggiungiCommento')}</p>
             <div className={`form-page form-page--xl ${styles.comment}`}>
              <Form.Control
                as={'textarea'}
                rows={3}
                 placeholder={t('feedback.labels.placeholder')}
                value={comment}
                disabled={loading}
                onChange={(e) => setComment(e.target.value)}
              />
            </div>
          </Else>
        </If>
      </Modal.Body>
      <Modal.Footer>
        <Button variant={'secondary'} className="tootor-button tootor-button--basic" onClick={() => onHide(false)} disabled={loading && !feedbackSent}>
           {feedbackSent ? t('labels.chiudi') : t('labels.annulla')}
        </Button>
        <When condition={!feedbackSent}>
            <Button
              variant={'primary'}
              className="tootor-button tootor-button--secondary"
              disabled={!rating1 || !rating2 || !rating3 || loading}
            onClick={() => {
              setLoading(true);
              database
                .collection('feedbacks')
                .add({
                  uid: currentUser.uid,
                  name: getFullName(),
                  questions: questions.map((q) => q.label),
                  ratings: questions.map((q) => q.rating),
                  comment: comment || '',
                  created: fv.serverTimestamp(),
                })
                .then(() => setFeedbackSent(true))
                .catch(console.error)
                .finally(() => setLoading(false));
            }}
          >
            {loading ? <>{t('labels.attendi')}&hellip;</> : t('labels.invia')}
          </Button>
        </When>
      </Modal.Footer>
    </Modal>
  );
};

export { FeedbackComponent as Feedback };
